import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { PerspectiveCamera, OrbitControls } from '@react-three/drei';
import { useThree, useFrame } from '@react-three/fiber';

export default function LevelCamera({
  target = [0, 0, 0],
  yawDeg = -30,
  pitchDeg = 12,
  radius = 2,
  fov = 80,
  enabled = true,
  panCommand = null,            // (keeps your old yaw/pitch pan)
  lookAtCommand = null,         // NEW: { target: [x,y,z], durationSec }
}) {
  const controlsRef = useRef(null);
  const { camera } = useThree();
  const tgt = new THREE.Vector3(...target);

  // initial placement (same as before)
  useEffect(() => {
    const yaw = THREE.MathUtils.degToRad(yawDeg);
    const pitch = THREE.MathUtils.degToRad(pitchDeg);
    const pos = new THREE.Vector3(
      Math.sin(yaw) * Math.cos(pitch) * radius + tgt.x,
      Math.sin(pitch) * radius + tgt.y,
      -Math.cos(yaw) * Math.cos(pitch) * radius + tgt.z
    );
    camera.position.copy(pos);
    camera.up.set(0, 1, 0);
    camera.lookAt(tgt);
    camera.updateProjectionMatrix();
    if (controlsRef.current) {
      controlsRef.current.target.copy(tgt);
      controlsRef.current.update();
    }
  }, [yawDeg, pitchDeg, radius, camera, tgt.x, tgt.y, tgt.z]);

  // ---------- KEEP: yaw/pitch "orbit" pan (moves camera around target)
  const panState = useRef(null);
  useEffect(() => {
    if (!panCommand || !controlsRef.current) return;
    const { yawDeg: y, pitchDeg: p, durationSec = 1.2 } = panCommand;

    const startPos = camera.position.clone();
    const startQuat = camera.quaternion.clone();
    const pivot = controlsRef.current.target.clone(); // ← use current controls target

    const yaw = THREE.MathUtils.degToRad(y);
    const pitch = THREE.MathUtils.degToRad(p);
    const newDir = new THREE.Vector3(
      Math.sin(yaw) * Math.cos(pitch),
      Math.sin(pitch),
      -Math.cos(yaw) * Math.cos(pitch)
    ).normalize();

    const r = startPos.distanceTo(pivot);
    const endPos = pivot.clone().add(newDir.multiplyScalar(r));

    panState.current = {
      mode: 'orbit',
      t0: performance.now(),
      dur: durationSec * 1000,
      fromPos: startPos,
      fromQuat: startQuat,
      toPos: endPos,
      pivot,
    };

    controlsRef.current.enabled = false;
  }, [panCommand, camera, tgt]);

  // ---------- NEW: target-only "pan" (keeps camera position fixed, moves lookAt)
  const lookState = useRef(null);
  useEffect(() => {
    if (!lookAtCommand || !controlsRef.current) return;
    const { target: toTarget, durationSec = 1.2 } = lookAtCommand;

    const controls = controlsRef.current;
    const startTarget = controls.target.clone();
    const endTarget = new THREE.Vector3(...toTarget);

    lookState.current = {
      t0: performance.now(),
      dur: durationSec * 1000,
      fromTarget: startTarget,
      toTarget: endTarget,
      fromQuat: camera.quaternion.clone(),
    };

    controls.enabled = false; // freeze user input during the pan
  }, [lookAtCommand, camera]);

  useFrame(() => {
    const now = performance.now();

    // Handle "orbit" pan (moves camera position)
    if (panState.current) {
      const s = panState.current;
      const t = Math.min(1, (now - s.t0) / s.dur);
      const ease = t * t * (3 - 2 * t);

      camera.position.lerpVectors(s.fromPos, s.toPos, ease);

      const lookMat = new THREE.Matrix4().lookAt(camera.position, s.pivot, new THREE.Vector3(0, 1, 0));
      const lookQuat = new THREE.Quaternion().setFromRotationMatrix(lookMat);
      camera.quaternion.slerpQuaternions(s.fromQuat, lookQuat, ease);
      camera.updateProjectionMatrix();
      controlsRef.current?.update();

      if (t >= 1) {
        controlsRef.current.enabled = false;
        panState.current = null;
      }
    }

    // Handle "lookAt" pan (keeps camera position, animates target)
    if (lookState.current && controlsRef.current) {
      const s = lookState.current;
      const t = Math.min(1, (now - s.t0) / s.dur);
      const ease = t * t * (3 - 2 * t);

      const currentTarget = new THREE.Vector3().lerpVectors(s.fromTarget, s.toTarget, ease);
      controlsRef.current.target.copy(currentTarget);

      // Rotate camera to face the current target without moving position
      const lookMat = new THREE.Matrix4().lookAt(camera.position, currentTarget, new THREE.Vector3(0,1,0));
      const lookQuat = new THREE.Quaternion().setFromRotationMatrix(lookMat);
      camera.quaternion.slerpQuaternions(s.fromQuat, lookQuat, ease);
      camera.updateProjectionMatrix();
      controlsRef.current.update();

      if (t >= 1) {
        controlsRef.current.enabled = false; // keep disabled during video
        lookState.current = null;
      }
    }
  });

  return (
    <>
      <PerspectiveCamera makeDefault fov={fov} />
      <OrbitControls
        ref={controlsRef}
        enabled={enabled}
        enablePan={false}
        minDistance={1}
        maxDistance={1}
        minPolarAngle={THREE.MathUtils.degToRad(50)}
        maxPolarAngle={THREE.MathUtils.degToRad(112)}
        enableDamping
        dampingFactor={0.08}
      />
    </>
  );
}
